/* global globalThis */
import { canonical } from './identity';
const DATABASE = 'northstar-test-attempts';
const STORE = 'attempts';
const DEADLINE_MS = 10000;
const copy = value => JSON.parse(JSON.stringify(value));

export async function createAttemptJournal(userId) {
  if (typeof userId !== 'string' || !userId || userId.length > 200) throw new Error('Sign in before opening the calibration recovery journal.');
  if (!globalThis.indexedDB) throw new Error('This browser cannot retain a calibration recovery journal.');
  const database = await new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error, value) => { if (settled) { value?.close(); return; } settled = true; clearTimeout(timer); if (error) reject(error); else resolve(value); };
    const timer = setTimeout(() => finish(new Error('Opening the recovery journal timed out.')), DEADLINE_MS);
    const request = globalThis.indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => { if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'key' }); };
    request.onsuccess = () => finish(null, request.result);
    request.onerror = () => finish(new Error('The calibration recovery journal could not open.'));
    request.onblocked = () => finish(new Error('Close other Northstar pages before opening the recovery journal.'));
  });
  let closed = false;
  database.onversionchange = () => { closed = true; database.close(); };
  const transaction = (mode, action, signal) => new Promise((resolve, reject) => {
    if (closed) { reject(new Error('The recovery journal is closed.')); return; }
    let result;
    try {
      const operation = database.transaction(STORE, mode), store = operation.objectStore(STORE); let settled = false;
      const finish = error => { if (settled) return; settled = true; clearTimeout(timer); signal?.removeEventListener('abort', abort); if (error) reject(error); else resolve(result); };
      const abort = () => { try { operation.abort(); } catch {} finish(new Error('Recovery journal operation interrupted.')); };
      const timer = setTimeout(() => { try { operation.abort(); } catch {} finish(new Error('Recovery journal operation timed out.')); }, DEADLINE_MS);
      operation.oncomplete = () => finish();
      operation.onerror = operation.onabort = () => finish(new Error('The recovery journal could not retain this attempt. Keep this page open and export its report.'));
      signal?.addEventListener('abort', abort, { once: true }); if (signal?.aborted) { abort(); return; }
      action(store, value => { result = value; }, operation);
    } catch (error) { reject(new Error(`Recovery journal unavailable: ${error.message}`)); }
  });
  const keyFor = id => `${userId}:${id}`;
  return {
    async save(report, { finalized = false, signal } = {}) {
      if (!report || report.schemaVersion !== 'northstar-pilot/2' || !/^[a-f0-9-]{36}$/i.test(report.clientAttemptId || '')) throw new Error('Invalid attempt identity for recovery.');
      const snapshot = copy(report);
      if (JSON.stringify(snapshot).length > 2 * 1024 * 1024) throw new Error('Recovery report exceeds 2 MiB.');
      return transaction('readwrite', (store, done, operation) => {
        const key = keyFor(snapshot.clientAttemptId), request = store.get(key);
        request.onsuccess = () => {
          const existing = request.result;
          if (existing?.finalized && JSON.stringify(existing.report) !== JSON.stringify(snapshot)) { operation.abort(); return; }
          if (existing && canonical(existing.report?.browser?.configurationPackage) !== canonical(snapshot.browser?.configurationPackage)) { operation.abort(); return; }
          store.put({ key, owner: userId, report: snapshot, finalized: existing?.finalized || finalized, savedAt: new Date().toISOString() }); done(true);
        };
      }, signal);
    },
    list() { return transaction('readonly', (store, done) => { const request = store.getAll(); request.onsuccess = () => done(request.result.filter(entry => entry.owner === userId).map(({ report, finalized, savedAt }) => ({ report, finalized, savedAt })).sort((a, b) => b.savedAt.localeCompare(a.savedAt))); }); },
    remove(id) { return transaction('readwrite', (store, done) => { store.delete(keyFor(id)); done(true); }); },
    close() { closed = true; database.close(); },
  };
}

export function interruptRecoveredReport(report) {
  const recovered = copy(report), at = new Date().toISOString();
  recovered.outcome = 'aborted'; recovered.endedAt = at;
  recovered.events.push({ sequence: recovered.events.length, at, type: 'aborted', message: 'Recovered an unfinished browser attempt. Physical device state is unknown; reacquire/setup before a new attempt.' });
  recovered.notes = `${recovered.notes} Recovered after interruption; no successful physical completion is inferred.`.slice(0, 4000);
  return recovered;
}
